import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, input, linkedSignal, signal, viewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25MultiselectModule } from "../../s25-multiselect/s25-multiselect.module";
import { MultiselectModelI } from "../../s25-multiselect/s25.multiselect.component";
import { S25ItemI } from "../../../pojo/S25ItemI";
import { s25Resource } from "../../../util/s25.resource";
import { S25Util } from "../../../util/s25-util";
import { AlertService } from "../../../services/alert.service";
import { StateService } from "../../../services/state.service";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { S25FadingSuccessMsgComponent } from "../../s25-fading-success-msg/s25.fading.success.msg.component";
import { S25LoadingInlineStaticComponent } from "../../s25-loading/s25.loading.inline.static.component";
import { S25ModalModule } from "../../s25-modal/s25.modal.module";
import { S25ModalComponent } from "../../s25-modal/s25.modal.component";
import { ProgressNode, ProgressResponse, TrailApi, TrailProgress } from "./trail.api";
import { TrailLabels } from "./trail.labels";
import { progressStatusLabel } from "./trail.progress.display";
import { TrailProgressTreeComponent } from "./trail.progress.tree.component";

interface CurriculumOption {
    defnId: number;
    name: string;
}

function parseSearchNumber(raw: string | undefined): number | null {
    if (raw == null || String(raw).trim() === "") return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
}

@TypeManagerDecorator("s25-ng-trail-admin-progress")
@Component({
    selector: "s25-ng-trail-admin-progress",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        S25ButtonComponent,
        S25FadingSuccessMsgComponent,
        S25LoadingInlineStaticComponent,
        S25ModalModule,
        S25MultiselectModule,
        TrailProgressTreeComponent,
    ],
    host: { class: "trail-admin-progress" },
    template: `
        <div class="c-padding--double">
            <h1>{{ labels.PROGRESS }} Admin</h1>
            <p class="c-text--secondary c-margin-bottom--single">
                View student progress, grant or revoke approval requirements, and override any requirement leaf.
            </p>

            <div class="trail-admin-progress__selectors c-margin-bottom--single">
                <label
                    id="trail-admin-progress-student-label"
                    class="trail-admin-progress__student-label"
                    data-id="student-label"
                >
                    Student
                </label>
                <label
                    class="trail-admin-progress__curric-label"
                    data-id="curriculum-label"
                    for="trail-admin-progress-curriculum"
                >
                    {{ labels.CURRICULUM }}
                </label>

                <div
                    class="trail-admin-progress__student-control"
                    data-id="student-picker"
                    role="group"
                    aria-labelledby="trail-admin-progress-student-label"
                >
                    <s25-ng-multiselect-search-criteria
                        [type]="'contacts'"
                        [buttonText]="'Search contacts'"
                        [modelBean]="contactPickerBean"
                        [selectedItems]="contactPickerBean.selectedItems ?? []"
                        [showResults]="true"
                        [popoverOnBody]="true"
                        [useSecurity]="false"
                        [singleSelect]="true"
                        (close)="onContactSelectionChange($event)"
                        (changed)="onContactSelectionChange($event)"
                    />
                </div>
                <div class="trail-admin-progress__curric-control" data-id="curriculum-picker">
                    @if (!selectedContId()) {
                        <span class="trail-admin-progress__curric-hint c-text--secondary">Select a student first</span>
                    } @else if (curriculaResource.isLoading()) {
                        <span class="trail-admin-progress__curric-hint c-text--secondary">Loading…</span>
                    } @else {
                        <select
                            id="trail-admin-progress-curriculum"
                            class="c-input"
                            [ngModel]="selectedDefnId()"
                            (ngModelChange)="onCurriculumChange($event)"
                        >
                            <option [ngValue]="null">Select {{ labels.CURRICULUM.toLowerCase() }}</option>
                            @for (curriculum of curriculumOptions(); track curriculum.defnId) {
                                <option [ngValue]="curriculum.defnId">{{ curriculum.name }}</option>
                            }
                        </select>
                    }
                </div>
                <s25-ng-button
                    class="trail-admin-progress__refresh"
                    data-id="refresh-list"
                    [type]="'outline'"
                    [onClick]="refreshList"
                >
                    Refresh
                </s25-ng-button>
            </div>
            <s25-ng-fading-success-msg class="trail-admin-progress__success" [(message)]="approvalSuccessMessage" />

            @if (selectedContId() && selectedDefnId()) {
                @if (progressResource.isLoading()) {
                    <s25-ng-loading-inline-static [text]="'Loading progress…'" />
                } @else if (progressResource.error()) {
                    <p class="c-text--error">Unable to load progress.</p>
                } @else if (progressData()) {
                    <header class="trail-admin-progress__header c-margin-bottom--single">
                        <h2>{{ progressData()!.displayName }}</h2>
                        @if (progressData()!.endingAwardDisplayName) {
                            <span class="trail-admin-progress__award">{{
                                progressData()!.endingAwardDisplayName
                            }}</span>
                        }
                        @if (progressData()!.defnEarned) {
                            <span class="trail-admin-progress__earned-badge">{{ labels.AWARD_EARNED }}</span>
                        }
                    </header>

                    @if (progressData()!.curriculumTree) {
                        <section class="c-margin-bottom--single">
                            <h3>{{ labels.ENDING_AWARD }} tree</h3>
                            <div class="trail-admin-progress__tree">
                                <s25-ng-trail-progress-tree
                                    [node]="progressData()!.curriculumTree"
                                    [actionsTemplate]="adminActions"
                                />
                            </div>
                        </section>
                    }

                    <h3>{{ labels.TRAILS }}</h3>
                    <ul class="trail-admin-progress__trail-list">
                        @for (trail of sortedTrails(); track trail.trailId) {
                            <li class="trail-admin-progress__trail">
                                <div class="trail-admin-progress__trail-header">
                                    <span class="trail-admin-progress__trail-name">{{ trail.name }}</span>
                                    @if (trail.focusAreaName) {
                                        <span class="trail-admin-progress__focus-badge">{{ trail.focusAreaName }}</span>
                                    }
                                    <span
                                        class="trail-admin-progress__status-badge trail-admin-progress__status-badge--{{
                                            trail.status
                                        }}"
                                    >
                                        {{ progressStatusLabel(trail.status) }}
                                    </span>
                                    @if (trail.summitEarned) {
                                        <span class="trail-admin-progress__summit-indicator">{{
                                            labels.SUMMIT_EARNED
                                        }}</span>
                                    }
                                </div>
                                <div class="trail-admin-progress__tree">
                                    <s25-ng-trail-progress-tree [node]="trail.tree" [actionsTemplate]="adminActions" />
                                </div>
                            </li>
                        }
                    </ul>
                }
            } @else {
                <p class="c-text--secondary">
                    Select a student and {{ labels.CURRICULUM.toLowerCase() }} to view progress.
                </p>
            }
        </div>

        <ng-template #adminActions let-node>
            @if (node.leafType === "APPROVAL") {
                @if (node.status !== "satisfied") {
                    <s25-ng-button [type]="'outline'" (clicked)="grantApproval(node)">Grant</s25-ng-button>
                } @else {
                    <s25-ng-button [type]="'danger--outline'" (clicked)="revokeApproval(node)"> Revoke </s25-ng-button>
                }
            }
            @if (node.nodeType === "requirement") {
                @if (node.override?.overridden) {
                    @if (node.override.reason) {
                        <span class="trail-admin-progress__override-reason">{{ node.override.reason }}</span>
                    }
                    <s25-ng-button [type]="'danger--outline'" (clicked)="clearOverride(node)">
                        {{ labels.CLEAR_OVERRIDE }}
                    </s25-ng-button>
                } @else {
                    <s25-ng-button [type]="'outline'" (clicked)="overrideRequirement(node)">
                        {{ labels.OVERRIDE }}
                    </s25-ng-button>
                }
            }
        </ng-template>

        <s25-ng-modal #reasonModal [title]="reasonModalTitle()" [type]="'custom'" [size]="'xs'">
            <ng-template #s25ModalBody>
                <label class="trail-admin-progress__reason-label" for="trail-admin-progress-reason">
                    {{ reasonModalLabel() }} *
                </label>
                <textarea
                    id="trail-admin-progress-reason"
                    class="c-input"
                    rows="3"
                    [ngModel]="reasonDraft()"
                    (ngModelChange)="reasonDraft.set($event)"
                ></textarea>
            </ng-template>
            <ng-template #s25ModalFooter>
                <div class="footerContainer">
                    <s25-ng-button
                        [type]="'primary'"
                        [disabled]="!reasonDraft().trim()"
                        (clicked)="confirmReasonModal()"
                    >
                        {{ reasonModalConfirmLabel() }}
                    </s25-ng-button>
                    <s25-ng-button [type]="'danger--outline'" (clicked)="reasonModal.close()">Cancel</s25-ng-button>
                </div>
            </ng-template>
        </s25-ng-modal>
    `,
    styles: [
        `
            :host {
                display: block;
            }
            .trail-admin-progress__selectors {
                display: grid;
                grid-template-columns: max-content max-content max-content;
                grid-template-rows: auto auto;
                column-gap: 1.25rem;
                row-gap: 0.25rem;
                align-items: start;
                justify-content: start;
            }
            .trail-admin-progress__success {
                display: block;
                min-height: 1.5rem;
                margin-top: 0.5rem;
            }
            .trail-admin-progress__student-label,
            .trail-admin-progress__curric-label {
                margin: 0;
                font-weight: 600;
                line-height: 1.2;
            }
            .trail-admin-progress__student-label {
                grid-column: 1;
                grid-row: 1;
            }
            .trail-admin-progress__curric-label {
                grid-column: 2;
                grid-row: 1;
            }
            .trail-admin-progress__student-control {
                grid-column: 1;
                grid-row: 2;
                min-width: 0;
                min-height: 2.25rem;
            }
            .trail-admin-progress__curric-control {
                grid-column: 2;
                grid-row: 2;
                display: flex;
                align-items: center;
                min-height: 2.25rem;
            }
            .trail-admin-progress__refresh {
                grid-column: 3;
                grid-row: 2;
            }
            .trail-admin-progress__student-control s25-ng-multiselect-search-criteria {
                display: block;
                width: max-content;
                max-width: min(28rem, 100%);
            }
            :host ::ng-deep .trail-admin-progress__student-control .s25-multiselect,
            :host ::ng-deep .trail-admin-progress__student-control .s25-multiselect-result {
                width: auto;
            }
            :host ::ng-deep .trail-admin-progress__student-control .s25-multiselect-result:not(:has(.c-tagItem)) {
                display: none;
            }
            .trail-admin-progress__curric-control select {
                min-width: 14rem;
            }
            .trail-admin-progress__curric-hint {
                display: flex;
                align-items: center;
                min-height: 2.25rem;
            }
            .trail-admin-progress__header {
                display: flex;
                align-items: center;
                gap: 0.75rem;
                flex-wrap: wrap;
            }
            .trail-admin-progress__header h2 {
                margin: 0;
            }
            .trail-admin-progress__award {
                font-size: 0.85rem;
                color: var(--color--text-muted);
            }
            .trail-admin-progress__earned-badge {
                background: var(--cn-navy, #1f4486);
                color: var(--color--primary-fg, #fff);
                padding: 0.2rem 0.6rem;
                border-radius: 4px;
                font-size: 0.8rem;
                font-weight: 600;
            }
            .trail-admin-progress__trail-list {
                list-style: none;
                margin: 0;
                padding: 0;
                display: flex;
                flex-direction: column;
                gap: 1rem;
            }
            .trail-admin-progress__trail {
                border: 1px solid var(--color--border);
                border-radius: 8px;
                padding: 0.75rem 1rem;
                background: var(--color--surface-2-bg);
            }
            .trail-admin-progress__trail-header {
                display: flex;
                align-items: center;
                gap: 0.5rem;
                flex-wrap: wrap;
                margin-bottom: 0.5rem;
            }
            .trail-admin-progress__trail-name {
                font-weight: 600;
                font-size: 0.95rem;
            }
            .trail-admin-progress__focus-badge {
                background: var(--cn-navy-100, #e8eaf0);
                padding: 0.15rem 0.5rem;
                border-radius: 4px;
                font-size: 0.78rem;
                font-weight: 500;
            }
            .trail-admin-progress__status-badge {
                padding: 0.15rem 0.45rem;
                border-radius: 4px;
                font-size: 0.75rem;
                font-weight: 600;
            }
            .trail-admin-progress__status-badge--satisfied {
                background: #d4edda;
                color: #155724;
            }
            .trail-admin-progress__status-badge--in_progress {
                background: #fff3cd;
                color: #856404;
            }
            .trail-admin-progress__status-badge--not_started {
                background: #e2e3e5;
                color: #383d41;
            }
            .trail-admin-progress__status-badge--not_yet_open {
                background: #d6d8db;
                color: #383d41;
            }
            .trail-admin-progress__status-badge--expired {
                background: #f8d7da;
                color: #721c24;
            }
            .trail-admin-progress__status-badge--locked {
                background: #d6d8db;
                color: #383d41;
            }
            .trail-admin-progress__summit-indicator {
                font-size: 0.78rem;
                color: var(--cn-navy, #1f4486);
                font-weight: 600;
            }
            .trail-admin-progress__override-reason {
                font-size: 0.78rem;
                color: var(--color--text-muted);
            }
            .trail-admin-progress__reason-label {
                display: block;
                margin-bottom: 0.35rem;
                font-weight: 600;
            }
            .footerContainer {
                display: flex;
                gap: 0.5em;
                justify-content: flex-end;
            }
        `,
    ],
})
export class TrailAdminProgressComponent {
    readonly labels = TrailLabels;
    readonly progressStatusLabel = progressStatusLabel;

    readonly contId = input<number | null>(parseSearchNumber(StateService.getSearch().contId));
    readonly defnId = input<number | null>(parseSearchNumber(StateService.getSearch().defnId));
    readonly contactName = input<string | undefined>(StateService.getSearch().contactName || undefined);
    readonly selectedContId = linkedSignal(() => this.contId());
    readonly selectedDefnId = linkedSignal(() => this.defnId());
    readonly approvalSuccessMessage = signal("");
    private readonly reloadGen = signal(0);
    readonly reasonModal = viewChild.required<S25ModalComponent<string | undefined>>("reasonModal");
    readonly reasonDraft = signal("");
    readonly reasonModalTitle = signal("");
    readonly reasonModalLabel = signal("");
    readonly reasonModalConfirmLabel = signal("");

    contactPickerBean: MultiselectModelI = {
        uuid: "trail-admin-progress-contact",
        singleSelect: true,
        title: "Contacts",
        selectedItems: [],
    };

    constructor() {
        const seededContId = this.selectedContId();
        if (seededContId != null) {
            this.contactPickerBean.selectedItems = [
                {
                    itemId: seededContId,
                    itemName: this.contactName() || "Selected student",
                },
            ];
        }
    }

    readonly curriculaResource = s25Resource({
        if: () => this.selectedContId() != null,
        params: () => ({
            contId: this.selectedContId()!,
            gen: this.reloadGen(),
        }),
        loader: ({ params }) => TrailApi.listCurriculaForStudent(params.contId),
    });

    readonly curriculumOptions = computed((): CurriculumOption[] => {
        const raw = this.curriculaResource.value() ?? [];
        return raw.map((defn) => {
            const label = defn.displayName || defn.name || "";
            return {
                defnId: defn.defnId,
                name: defn.cohortCode ? `${label} (${defn.cohortCode})` : label,
            };
        });
    });

    readonly progressResource = s25Resource({
        if: () => this.selectedContId() != null && this.selectedDefnId() != null,
        params: () => ({
            defnId: this.selectedDefnId(),
            contId: this.selectedContId(),
            gen: this.reloadGen(),
        }),
        loader: ({ params }) => TrailApi.getAdminProgress(params.defnId!, params.contId!),
    });

    readonly progressData = computed((): ProgressResponse | undefined => this.progressResource.value());

    readonly sortedTrails = computed((): TrailProgress[] => {
        const data = this.progressData();
        if (!data?.trails) return [];
        return [...data.trails].sort((trailA, trailB) => {
            if (trailA.topoLevel !== trailB.topoLevel) return trailA.topoLevel - trailB.topoLevel;
            const sortA = trailA.catalogSortOrder ?? Number.MAX_SAFE_INTEGER;
            const sortB = trailB.catalogSortOrder ?? Number.MAX_SAFE_INTEGER;
            if (sortA !== sortB) return sortA - sortB;
            return (trailA.name ?? "").localeCompare(trailB.name ?? "");
        });
    });

    onCurriculumChange(defnId: number | null): void {
        this.selectedDefnId.set(defnId);
        this.syncUrl();
    }

    refreshList = () => {
        this.reloadGen.update((count) => count + 1);
    };

    onContactSelectionChange(selected: S25ItemI[] | undefined): void {
        const items = selected ?? [];
        const first = items[0];
        const contactItemId = first?.itemId != null ? Number(first.itemId) : null;
        const nextContId = Number.isFinite(contactItemId) ? contactItemId : null;
        this.contactPickerBean.selectedItems = [...items];
        if (nextContId === this.selectedContId()) {
            return;
        }
        this.selectedDefnId.set(null);
        this.selectedContId.set(nextContId);
        this.reloadGen.update((count) => count + 1);
        this.syncUrl();
    }

    async grantApproval(node: ProgressNode): Promise<void> {
        const reason = await this.promptReason(
            this.labels.GRANT_APPROVAL,
            this.labels.APPROVAL_REASON,
            this.labels.GRANT_APPROVAL,
        );
        if (!reason) return;
        try {
            await TrailApi.createRequirementApproval(node.nodeId, this.selectedContId()!, reason.trim());
            await TrailApi.evaluateStudents([this.selectedContId()!]);
            this.reloadGen.update((count) => count + 1);
            this.approvalSuccessMessage.set("Approval granted.");
        } catch (caughtError) {
            await AlertService.alert(S25Util.errorText(caughtError));
        }
    }

    async revokeApproval(node: ProgressNode): Promise<void> {
        if (!(await AlertService.confirm("Revoke this approval?"))) return;
        try {
            await TrailApi.revokeRequirementApproval(node.nodeId, this.selectedContId()!);
            await TrailApi.evaluateStudents([this.selectedContId()!]);
            this.reloadGen.update((count) => count + 1);
            this.approvalSuccessMessage.set("Approval revoked.");
        } catch (caughtError) {
            await AlertService.alert(S25Util.errorText(caughtError));
        }
    }

    async promptReason(title: string, fieldLabel: string, confirmLabel: string): Promise<string | undefined> {
        this.reasonDraft.set("");
        this.reasonModalTitle.set(title);
        this.reasonModalLabel.set(fieldLabel);
        this.reasonModalConfirmLabel.set(confirmLabel);
        return this.reasonModal().open();
    }

    confirmReasonModal(): void {
        const reason = this.reasonDraft().trim();
        if (!reason) return;
        void this.reasonModal().close(reason);
    }

    async overrideRequirement(node: ProgressNode): Promise<void> {
        const reason = await this.promptReason(this.labels.OVERRIDE, this.labels.OVERRIDE_REASON, this.labels.OVERRIDE);
        if (!reason) return;
        try {
            await TrailApi.createRequirementOverride(node.nodeId, this.selectedContId()!, reason.trim());
            this.reloadGen.update((count) => count + 1);
            this.approvalSuccessMessage.set("Override created.");
        } catch (caughtError) {
            await AlertService.alert(S25Util.errorText(caughtError));
        }
    }

    async clearOverride(node: ProgressNode): Promise<void> {
        if (!(await AlertService.confirm("Clear this override?"))) return;
        try {
            await TrailApi.revokeRequirementOverride(node.nodeId, this.selectedContId()!);
            this.reloadGen.update((count) => count + 1);
            this.approvalSuccessMessage.set("Override cleared.");
        } catch (caughtError) {
            await AlertService.alert(S25Util.errorText(caughtError));
        }
    }

    private syncUrl(): void {
        const selectedContact = this.contactPickerBean.selectedItems?.[0];
        StateService.setSearch({
            defnId: this.selectedDefnId() ?? undefined,
            contId: this.selectedContId() ?? undefined,
            contactName: selectedContact?.itemName,
        });
    }
}
